(() => {
  'use strict';
  const id = value => String(value ?? '').split('/').pop();
  const options = variant => variant.options || [];
  const WRIST_STORAGE_KEY = 'lbdb-classic-wrist-cm:v1';
  const WRIST_CHANGE_EVENT = 'lbdb:classic-wrist-change';
  let wristInMemory = null;
  let wristStorageWriteFailed = false;
  const validSavedWrist = value => /^\d{2}$/.test(String(value ?? '')) && Number(value) >= 14 && Number(value) <= 23;
  function recallWrist() {
    if (wristStorageWriteFailed && validSavedWrist(wristInMemory)) return Number(wristInMemory);
    try {
      const stored = window.sessionStorage.getItem(WRIST_STORAGE_KEY);
      if (validSavedWrist(stored)) return (wristInMemory = Number(stored));
      wristInMemory = null;
    } catch (_) { if (validSavedWrist(wristInMemory)) return Number(wristInMemory); }
    return null;
  }
  function rememberWrist(value) {
    if (!validSavedWrist(value)) return false;
    wristInMemory = Number(value);
    try { window.sessionStorage.setItem(WRIST_STORAGE_KEY, String(wristInMemory)); wristStorageWriteFailed = false; } catch (_) { wristStorageWriteFailed = true; }
    return true;
  }
  const cm = value => { const n = Number(String(value ?? '').trim().replace(',', '.')); return Number.isFinite(n) && n > 0 && n <= 100 ? n : null; };
  const usesInches = country => ['US', 'GB'].includes(country);
  const measuredCm = (value, unit) => {
    const raw = String(value ?? '').trim().replace(',', '.');
    if (!/^\d+(?:\.\d+)?$/.test(raw)) return NaN;
    const number = Number(raw) * (unit === 'in' ? 2.54 : 1);
    // La précision affichée est le dixième de cm : éviter qu'une conversion arrondie dépasse une taille exacte.
    return Math.round(number * 10) / 10;
  };
  function parseLength(value) {
    let text = String(value ?? '').toLowerCase().trim().replace(/,/g, '.').replace(/centim(?:ètres?|etres?|eters?|etri|etro|etros?|etry|etrů|etre)/g, 'cm').replace(/\s+/g, ' ');
    text = text.replace(/^(?:de|from|da|von|fra|van|od|από|från|desde|de la)\s+/, '').replace(/\s+(?:à|to|a|bis|til|tot|do|έως|till|até|la|până la)\s+/g, '-');
    let match = text.match(/^(\d+(?:\.\d+)?)\s*(?:cm)?\s*\+\s*(\d+(?:\.\d+)?)\s*cm$/);
    if (match) { const min = cm(match[1]), delta = cm(match[2]); return min && delta && min + delta <= 100 ? { min, max: min + delta, type: 'extension' } : null; }
    match = text.match(/^(\d+(?:\.\d+)?)\s*(?:cm)?\s*[-–—]\s*(\d+(?:\.\d+)?)\s*cm$/);
    if (match) { const min = cm(match[1]), max = cm(match[2]); return min && max && max >= min ? { min, max, type: 'range' } : null; }
    match = text.match(/^(\d+(?:\.\d+)?)\s*cm$/);
    if (match) { const length = cm(match[1]); return length ? { min: length, max: length, type: 'fixed' } : null; }
    return null;
  }
  function lengthPosition(data) {
    const positions = (data.options || []).filter(option => option.values?.length && option.values.every(v => parseLength(v)));
    return positions.length === 1 ? positions[0].position - 1 : -1;
  }
  function fitsWrist(range, wrist, target) {
    // La limite de 2 cm inclut l'aisance : elle part du poignet, pas de la cible déjà augmentée.
    // Pour une plage ou une chaînette, comparer le réglage utile, pas la longueur maximale déployée.
    const fittedLength = Math.max(range.min, target);
    return fittedLength <= range.max && fittedLength - wrist <= 2;
  }
  function compareUsefulLengths(a, b, target) {
    // Une plage est conseillée pour la longueur que le client peut réellement régler :
    // 17 + 5 couvre ainsi la cible 19, alors que 20 + 5 commence déjà à 20.
    const aUseful = Math.max(a.min, target), bUseful = Math.max(b.min, target);
    if (aUseful !== bUseful) return aUseful - bUseful;
    // À longueur utile égale, éviter de conseiller une rallonge inutilement grande.
    if (a.max !== b.max) return a.max - b.max;
    // Puis préférer la plage la plus resserrée ; les deux règles sont indépendantes de l'ordre Shopify.
    if (a.min !== b.min) return b.min - a.min;
    // Les géométries strictement identiques ont un ordre stable, documenté et déterministe.
    const typeRank = { fixed: 0, range: 1, extension: 2 };
    if (typeRank[a.type] !== typeRank[b.type]) return typeRank[a.type] - typeRank[b.type];
    const aValue = String(a.value), bValue = String(b.value);
    return aValue < bValue ? -1 : aValue > bValue ? 1 : a.index - b.index;
  }
  function resolve(data, wrist, ease = 1, current = data.currentOptions) {
    if (!Number.isFinite(wrist) || wrist < 10 || wrist > 35 || ![0, 1, 2].includes(ease)) return { status: 'invalid' };
    const target = Math.round((wrist + ease) * 100) / 100;
    if (data.special) return { status: 'special', target };
    const position = lengthPosition(data);
    const variants = data.variants || [];
    if (position < 0) {
      const suspicious = (data.options || []).some(o => o.values?.some(v => /cm|centim|\d.*mm|\+/.test(v)));
      const min = cm(data.lengthMin), max = cm(data.lengthMax), single = cm(data.length);
      const low = min || single, high = max || single;
      const invalidMin = String(data.lengthMin ?? '').trim() !== '' && min === null;
      const invalidMax = String(data.lengthMax ?? '').trim() !== '' && max === null;
      if (suspicious || invalidMin || invalidMax || !low || !high || low > high) return { status: 'verify', target };
      // Sans variante de longueur, les deux bornes renseignées décrivent le réglage du bracelet.
      // Une case Ajustable absente ou fausse ne doit pas annuler cette plage (ex. Roland 17–21 cm).
      if (!fitsWrist({ min: low, max: high }, wrist, target)) return { status: 'outside', target };
      const exact = variants.find(v => options(v).every((value, index) => value === current?.[index]));
      if (!exact?.available) return { status: 'unavailable', target };
      return { status: low === high ? 'fixed' : data.elastic ? 'elastic' : 'range', target, min: low, max: high, value: low === high ? low + ' cm' : '', position: -1 };
    }
    if (!Array.isArray(current) || current.length !== data.options.length) return { status: 'verify', target };
    const records = data.options.find(o => o.position - 1 === position).values.map((value, index) => ({ value, index, ...parseLength(value) }));
    // Une borne globale ne permet pas d'inventer une rallonge propre à chaque taille fixe.
    if (data.adjustable && records.every(record => record.type === 'fixed')) return { status: 'verify', target };
    const compatible = records.filter(r => fitsWrist(r, wrist, target));
    compatible.sort((a, b) => compareUsefulLengths(a, b, target));
    if (!compatible.length) return { status: 'outside', target };
    const best = compatible[0];
    const candidates = variants.filter(v => options(v)[position] === best.value);
    const exact = candidates.filter(v => options(v).every((value, index) => index === position || value === current[index]));
    const available = exact.find(v => v.available === true);
    if (!available) return { status: exact.length ? 'unavailable' : 'combination', target, min: best.min, max: best.max, value: best.value };
    return { status: best.type === 'fixed' ? 'fixed' : best.type === 'extension' ? 'extension' : data.elastic ? 'elastic' : 'range', target, min: best.min, max: best.max, value: best.value, position, variantId: available.id };
  }
  class ClassicSizeGuide extends (typeof HTMLElement === 'undefined' ? class {} : HTMLElement) {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true; this._abort = new AbortController();
      try { this._data = JSON.parse(this.querySelector('[data-cb-data]').textContent); this._i18n = JSON.parse(this.querySelector('[data-cb-i18n]').textContent); } catch (_) { return; }
      this._wrist = this.querySelector('[data-cb-wrist]'); this._slider = this.querySelector('[data-cb-slider]'); this._apply = this.querySelector('[data-cb-apply]');
      if (!this._wrist || !this._slider || !this._apply) return; // Fragment antérieur encore en cache : l'aide manuelle reste accessible.
      this._unit = usesInches(this.dataset.country) ? 'in' : 'cm';
      this.querySelector('[data-cb-unit-label]').textContent = this._unit;
      this._slider.min = '14'; this._slider.max = '23'; this._slider.step = '1';
      this._slider.value = String(recallWrist() ?? 19); // Une mesure explicite prime sur le défaut, même avec un ancien fragment en cache.
      // Un fragment antérieur peut encore être en cache pendant la publication : aucune saisie ni clavier mobile.
      if (this._wrist.tagName === 'INPUT') {
        const reading = document.createElement('span'); reading.className = 'lbdb-cb-guide__wrist'; reading.setAttribute('data-cb-wrist', '');
        this._wrist.replaceWith(reading); this._wrist = reading;
      }
      for (const [selector, number] of [['[data-cb-min]', 14], ['[data-cb-max]', 23]]) this.querySelector(selector).textContent = this._format(number);
      this._syncFromSlider();
      this._slider.addEventListener('input', () => { this._syncFromSlider(); this._saveWrist(); this._render(); }, { signal: this._abort.signal });
      document.addEventListener(WRIST_CHANGE_EVENT, event => {
        if (event.detail?.source === this) return;
        const wrist = recallWrist();
        if (wrist !== null) { this._slider.value = String(wrist); this._syncFromSlider(); this._render(); }
      }, { signal: this._abort.signal });
      document.addEventListener('dialog:after-show', event => {
        if (event.target === this.closest('x-drawer')) this._restoreWrist();
      }, { signal: this._abort.signal });
      const drawer = this.closest('x-drawer');
      if (drawer && typeof MutationObserver !== 'undefined') {
        this._drawerObserver = new MutationObserver(() => {
          if (drawer.hasAttribute('open')) this._restoreWrist();
        });
        this._drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['open'] });
      }
      window.addEventListener?.('pageshow', () => this._restoreWrist(), { signal: this._abort.signal });
      this._apply.addEventListener('click', () => this._applyRecommendation(), { signal: this._abort.signal });
      this.querySelector('[data-cb-interactive]').hidden = false;
      const loadingHelp = this.querySelector('[data-cb-loading-help]'); if (loadingHelp) loadingHelp.hidden = true;
      window.customElements?.whenDefined('variant-picker').then(() => {
        let attempts = 0;
        const connect = () => {
          if (!this._connected) return;
          const picker = this._findPicker();
          if (!picker?.masterSelector?.form || !picker.selectedVariant) { if (++attempts < 80) this._timer = setTimeout(connect, 100); return; }
          this._picker = picker; picker.masterSelector.form.addEventListener('variant:change', () => this._render(), { signal: this._abort.signal }); this._render();
        }; connect();
      });
      document.addEventListener('variant:change', () => this._render(), { signal: this._abort.signal });
      this._render();
    }
    disconnectedCallback() { this._connected = false; this._abort?.abort(); this._drawerObserver?.disconnect(); clearTimeout(this._timer); }
    _saveWrist() {
      // Sauvegarder la mesure du poignet, jamais la variante ni la longueur du bracelet conseillé.
      if (rememberWrist(this._slider.value)) document.dispatchEvent?.(new CustomEvent(WRIST_CHANGE_EVENT, { detail: { source: this } }));
    }
    _restoreWrist() {
      this._slider.value = String(recallWrist() ?? 19);
      this._syncFromSlider(); this._render();
    }
    _syncFromSlider() {
      const number = Math.max(14, Math.min(23, Math.round(Number(this._slider.value) || 19)));
      this._slider.value = String(number);
      this._wrist.textContent = this._formatNumber(number, 0);
      this.querySelector('[data-cb-unit-label]').textContent = this._unit === 'in' ? 'in (' + this._formatMetric(number, 0) + ' cm)' : 'cm';
      this._updateTrack();
    }
    _updateTrack() {
      const number = Number(this._slider.value);
      this._slider.style.setProperty('--cb-progress', ((number - 14) / 9 * 100) + '%');
      this._slider.setAttribute('aria-valuetext', this._format(number));
    }
    _findPicker() {
      return Array.from(document.querySelectorAll('variant-picker[handle]')).find(picker => picker.getAttribute('handle') === this.dataset.handle && !picker.closest('x-drawer,quick-buy-drawer,dialog') && id(picker.product?.id) === id(this._data.productId)) || null;
    }
    _text(key) {
      // Shopify échappe les traductions avant leur sérialisation JSON. Décoder une seule fois,
      // puis conserver le rendu textContent : aucun HTML ni script n'est interprété.
      const entities = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", '#39': "'", '#x27': "'", '#34': '"', '#x22': '"' };
      return String(this._i18n[key] || '').replace(/&(amp|lt|gt|quot|apos|#39|#x27|#34|#x22);/gi, (_, entity) => entities[entity.toLowerCase()]);
    }
    _dataNow() {
      const product = this._picker?.product;
      return product?.variants ? { ...this._data, variants: product.variants, options: this._data.options.map((o, index) => ({ ...o, values: [...new Set(product.variants.map(v => v.options[index]))] })) } : this._data;
    }
    _resolution() {
      // La mesure canonique vient du curseur entier, jamais du texte converti en pouces.
      return resolve(this._dataNow(), Number(this._slider.value), 1, this._picker?.selectedVariant?.options || this._data.currentOptions);
    }
    _formatNumber(number, cmPrecision = 1) {
      // Arrondir uniquement la présentation, jamais la mesure enregistrée ni le conseil.
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', { minimumFractionDigits: this._unit === 'in' ? 1 : 0, maximumFractionDigits: this._unit === 'in' ? 1 : cmPrecision }).format(this._unit === 'in' ? number / 2.54 : number);
    }
    _formatMetric(number, precision = 1) {
      return new Intl.NumberFormat(document.documentElement.lang || 'fr', { minimumFractionDigits: 0, maximumFractionDigits: precision }).format(number);
    }
    _format(number) {
      const label = this._formatNumber(number) + ' ' + this._unit;
      return this._unit === 'in' ? label + ' (' + this._formatMetric(number) + ' cm)' : label;
    }
    _variantLabel(r) {
      const parsed = parseLength(r.value);
      if (!parsed) return r.min != null ? this._format(r.min) : '';
      if (parsed.type === 'extension') return this._format(parsed.min) + ' + ' + this._format(parsed.max - parsed.min);
      return parsed.type === 'range' ? this._format(parsed.min) + ' – ' + this._format(parsed.max) : this._format(parsed.min);
    }
    _render() {
      if (!this._picker) {
        const picker = this._findPicker();
        if (picker?.product?.variants && picker?.masterSelector?.form && picker.selectedVariant) this._picker = picker;
      }
      const result = this.querySelector('[data-cb-result]'); result.hidden = !this._slider.value;
      this._apply.hidden = true; this._apply.disabled = true;
      if (result.hidden) return;
      const r = this._resolution(), good = ['fixed', 'range', 'elastic', 'extension'].includes(r.status), rangeOnly = good && !r.value && r.max > r.min;
      result.dataset.state = good ? 'ready' : 'verify'; result.dataset.kind = r.status;
      this.querySelector('[data-cb-body]').textContent = this._text(good ? rangeOnly ? 'range_title' : 'size_title' : r.status === 'invalid' ? 'invalid_title' : r.status === 'unavailable' || r.status === 'combination' ? 'unavailable_title' : 'check_title');
      const label = rangeOnly ? this._format(r.min) + ' – ' + this._format(r.max) : this._variantLabel(r);
      const value = this.querySelector('[data-cb-value]'); value.textContent = good ? label : ''; value.hidden = !good;
      const note = this.querySelector('[data-cb-note]'); note.textContent = this._text(good ? r.status === 'elastic' ? 'elastic_note' : r.status === 'extension' ? 'extension_note' : rangeOnly ? 'range_note' : 'size_note' : r.status);
      const range = this.querySelector('[data-cb-range]'); range.textContent = good && r.value && r.max > r.min ? this._text('adjusts') + ' ' + this._format(r.min) + ' – ' + this._format(r.max) : ''; range.hidden = !range.textContent;
      const confirmOnly = good && r.position === -1 && Boolean(this._text('confirm_choice'));
      this._apply.hidden = !good || (!confirmOnly && (!r.variantId || !this._picker)); this._apply.disabled = this._apply.hidden;
      this.querySelector('[data-cb-apply-label]').textContent = confirmOnly ? this._text('confirm_choice') : this._text('apply_size').replace('{{ size }}', label);
    }
    _applyRecommendation() {
      const r = this._resolution(), picker = this._picker;
      if (r.position === -1 && ['fixed', 'range', 'elastic'].includes(r.status) && this._text('confirm_choice')) {
        this._saveWrist(); this.closest('x-drawer')?.hide?.(); picker?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        return; // Confirmation seule : ne jamais sélectionner une couleur ou inventer une variante de taille.
      }
      const target = picker?.product?.variants.find(v => id(v.id) === id(r.variantId));
      if (!target?.available || typeof picker.selectVariant !== 'function' || !picker.selectedVariant?.options || options(target).some((value, index) => index !== r.position && value !== picker.selectedVariant.options[index])) { this.querySelector('[data-cb-note]').textContent = this._text('selection_error'); return; }
      try { picker.selectVariant(Number(target.id)); } catch (_) { this.querySelector('[data-cb-note]').textContent = this._text('selection_error'); return; }
      if (id(picker.masterSelector?.value) !== id(target.id) || id(picker.selectedVariant?.id) !== id(target.id)) { this.querySelector('[data-cb-note]').textContent = this._text('selection_error'); return; }
      this._saveWrist();
      this.querySelector('[data-cb-note]').textContent = this._text('applied'); this.closest('x-drawer')?.hide?.(); picker.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    }
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = { parseLength, lengthPosition, resolve, ClassicSizeGuide, usesInches, measuredCm };
  if (typeof window !== 'undefined' && window.customElements && !window.customElements.get('lbdb-classic-size-guide')) window.customElements.define('lbdb-classic-size-guide', ClassicSizeGuide);
})();
